import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const CartContext = createContext();

const initialState = {
  cartItems: [],
  loading: true
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_CART_ITEMS':
      return {
        ...state,
        cartItems: action.payload,
        loading: false
      };
    case 'ADD_TO_CART':
      const existingItem = state.cartItems.find(item => item.product_id === action.payload.product_id);
      if (existingItem) {
        return {
          ...state,
          cartItems: state.cartItems.map(item =>
            item.product_id === action.payload.product_id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      } else {
        return {
          ...state,
          cartItems: [...state.cartItems, action.payload]
        };
      }
    case 'UPDATE_CART_ITEM':
      return {
        ...state,
        cartItems: state.cartItems.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        cartItems: state.cartItems.filter(item => item.id !== action.payload)
      };
    case 'CLEAR_CART':
      return {
        ...state,
        cartItems: []
      };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { isAuthenticated } = useAuth();
  const [notice, setNotice] = React.useState({ open: false, title: '', subtitle: '', variant: 'info' });
  const [authPrompt, setAuthPrompt] = React.useState({ open: false });

  const showNotice = (variant, title, subtitle = '', duration = 2200) => {
    setNotice({ open: true, title, subtitle, variant });
    setTimeout(() => setNotice(prev => ({ ...prev, open: false })), duration);
  };

  // Load cart items from server when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadCartItems();
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [isAuthenticated]);

  const loadCartItems = async () => {
    try {
      const response = await axios.get('/api/cart');
      dispatch({ type: 'SET_CART_ITEMS', payload: response.data.items || [] });
    } catch (error) {
      console.error('Failed to load cart items:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addToCart = async (product, quantity = 1) => {
    if (!isAuthenticated) {
      setAuthPrompt({ open: true });
      return { success: false, reason: 'auth_required' };
    }

    try {
      const response = await axios.post('/api/cart/add', {
        product_id: product.id,
        quantity
      });

      // Suppress success toast
      const title = 'Ajouté au panier';
      const name = product?.name_fr || product?.name_en || product?.name || '';
      const subtitle = name ? `${name} × ${quantity}` : `Quantité: ${quantity}`;
      showNotice('success', title, subtitle);

      // Reload cart items to get updated data
      await loadCartItems();
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de l\'ajout au panier';
      showNotice('warning', message);
      return { success: false, error: message };
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    if (!isAuthenticated) { return; }

    try {
      const response = await axios.put(`/api/cart/update/${itemId}`, {
        quantity
      });

      // Suppress success toast

      // Reload cart items to get updated data
      await loadCartItems();
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour du panier';
      showNotice('warning', message);
    }
  };

  const removeFromCart = async (itemId) => {
    if (!isAuthenticated) { return; }

    try {
      const response = await axios.delete(`/api/cart/remove/${itemId}`);

      // Suppress success toast

      // Reload cart items to get updated data
      await loadCartItems();
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la suppression du panier';
      showNotice('warning', message);
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) { return; }

    try {
      const response = await axios.delete('/api/cart/clear');

      // Suppress success toast

      dispatch({ type: 'CLEAR_CART' });
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors du vidage du panier';
      showNotice('warning', message);
    }
  };

  const getCartTotal = () => {
    return state.cartItems.reduce((total, item) => {
      const price = item.sale_price || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartItemCount = () => {
    return state.cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems: state.cartItems,
    loading: state.loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartItemCount,
    loadCartItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      {notice.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
          <div className={`pointer-events-auto rounded-2xl shadow-2xl px-6 py-4 text-center ${notice.variant==='success' ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-yellow-100/95 text-yellow-900 border border-yellow-500'}`}>
            <div className={`text-lg font-semibold ${notice.variant==='success' ? '' : ''}`}>{notice.title}</div>
            {notice.subtitle && <div className={`${notice.variant==='success' ? 'text-gray-300' : 'text-yellow-800'} mt-1 text-sm`}>{notice.subtitle}</div>}
          </div>
        </div>
      )}

      {authPrompt.open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 text-white rounded-2xl shadow-2xl w-[92%] max-w-md border border-gray-700">
            <div className="px-6 py-5">
              <div className="text-xl font-semibold mb-2">Connectez-vous pour continuer</div>
              <div className="text-gray-300 mb-6">Créez un compte ou connectez-vous pour ajouter des articles au panier.</div>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => { setAuthPrompt({ open: false }); window.location.href = '/login'; }}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-3 rounded-lg transition-colors"
                >
                  Se connecter
                </button>
                <button
                  onClick={() => { setAuthPrompt({ open: false }); window.location.href = '/register'; }}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg border border-gray-600 transition-colors"
                >
                  Créer un compte
                </button>
                <button
                  onClick={() => setAuthPrompt({ open: false })}
                  className="w-full text-gray-400 hover:text-white py-2"
                >
                  Continuer la navigation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 